import React, { useEffect, useMemo, useCallback } from 'react';
import _ from 'lodash';

import dayjs from 'dayjs';
import { useLoadingBackdrop } from 'hooks/useLoadingBackdrop';
import { Button, Link, Modal, Typography } from 'ui-kit';
import Table from 'components/Table';

import { useOwnerPayoutDetails } from 'api/owner_sdk';
import { Owner, PaymentPlan } from 'api/types/owner';
import { BalanceSign } from 'api/types/balances';
import { formatCurrency } from 'utils';
import { downloadListOfListsAsCSV } from 'utils/csv';

const DEFAULT_RECIPIENT_NAME = 'Store Owner';

const normalizeRecipientName = (name: string): string => {
  if (!name) return DEFAULT_RECIPIENT_NAME;
  const trimmed = name.trim();
  const lower = trimmed.toLowerCase();

  if (
    lower === 'storeowner' ||
    lower === 'store_owner' ||
    lower === 'store owner' ||
    lower === 'default store owner'
  ) {
    return DEFAULT_RECIPIENT_NAME;
  }

  return trimmed
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface IOrderProductDetailsModal {
  owner: Owner;
  storeId: number;
  payoutStartDate: Date;
  payoutEndDate: Date;
  handleClose: () => void;
}

const OrderProductDetailsModal = ({
  owner,
  storeId,
  payoutStartDate,
  payoutEndDate,
  handleClose,
}: IOrderProductDetailsModal) => {
  const [showLoading, hideLoading] = useLoadingBackdrop();

  const { data: payoutObject, isLoading } = useOwnerPayoutDetails({
    storeId,
    payoutStartDate,
    payoutEndDate,
  });

  useEffect(() => {
    if (isLoading) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [isLoading]);

  const modalTitle = useMemo(
    () => `Payout details for store ${payoutObject?.store?.store_name || ''}`,
    [payoutObject],
  );

  const showModal = useMemo(() => !isLoading, [isLoading]);

  const getOrderProductWholesaleTotal = useCallback((orderProduct: any) => {
    const refundedAmount = Number(orderProduct.amount_refunded || 0);
    if (refundedAmount > 0) return 0;

    const amountSplits = orderProduct.amount_splits || [];
    const quantity = Number(orderProduct.quantity) || 1;

    if (!Array.isArray(amountSplits)) return 0;

    return amountSplits.reduce((sum: number, wSplit: any) => {
      const unitAmount = Number(wSplit?.amount) || 0;
      return sum + unitAmount * quantity;
    }, 0);
  }, []);

  const { recipientList, recipientTotals, wholesaleRecipientList, wholesaleTotals } =
    useMemo(() => {
      if (!payoutObject?.order_products) {
        return {
          recipientList: [DEFAULT_RECIPIENT_NAME],
          recipientTotals: {},
          wholesaleRecipientList: [],
          wholesaleTotals: {},
        };
      }

      const recipientsSet = new Set<string>([DEFAULT_RECIPIENT_NAME]);
      const totals: Record<string, number> = {};

      const wholesaleSet = new Set<string>();
      const wTotals: Record<string, number> = {};

      payoutObject.order_products.forEach((order_product: any) => {
        const refundedAmount = Number(order_product.amount_refunded || 0);
        const hasRefund = refundedAmount > 0;

        const amountSplits = order_product.amount_splits || [];
        const quantity = Number(order_product.quantity) || 1;
        let currentWholesaleTotal = 0;

        if (Array.isArray(amountSplits)) {
          amountSplits.forEach((wSplit: any) => {
            if (wSplit?.recipient) {
              const name = normalizeRecipientName(wSplit.recipient);
              const unitAmount = Number(wSplit.amount) || 0;
              const totalAmount = hasRefund ? 0 : unitAmount * quantity;

              currentWholesaleTotal += totalAmount;
              wholesaleSet.add(name);
              wTotals[name] = (wTotals[name] || 0) + totalAmount;
            }
          });
        }

        const rawOwnerProfit = Number(order_product.owner_profit) || 0;
        const profitToSplit = Math.max(0, rawOwnerProfit - currentWholesaleTotal);
        const splits = order_product.profit_splits || [];

        if (Array.isArray(splits) && splits.length > 0) {
          splits.forEach((split: any) => {
            const name = normalizeRecipientName(split.recipient);
            const percent = Number(split.percents) || 0;
            const share = (profitToSplit * percent) / 100;

            recipientsSet.add(name);
            totals[name] = (totals[name] || 0) + share;
          });
        } else {
          recipientsSet.add(DEFAULT_RECIPIENT_NAME);
          totals[DEFAULT_RECIPIENT_NAME] = (totals[DEFAULT_RECIPIENT_NAME] || 0) + profitToSplit;
        }
      });

      return {
        recipientList: Array.from(recipientsSet).sort(),
        recipientTotals: totals,
        wholesaleRecipientList: Array.from(wholesaleSet).sort(),
        wholesaleTotals: wTotals,
      };
    }, [payoutObject]);

  const dynamicWholesaleColumns = useMemo(() => {
    if (!payoutObject) return [];

    return wholesaleRecipientList.map((recipientName) => {
      const totalForRecipient = wholesaleTotals[recipientName] || 0;

      return {
        title: (
          <div>
            {recipientName} (Wholesale)
            <br />
            <span style={{ fontSize: '12px', color: '#959097' }}>
              (Total: {formatCurrency(totalForRecipient, payoutObject.currency)})
            </span>
          </div>
        ),
        key: `wholesale_${recipientName}`,
        render: (_text: any, record: any) => {
          const refundedAmount = Number(record.amount_refunded || 0);

          if (refundedAmount > 0) {
            return formatCurrency(0, payoutObject.currency);
          }

          const amountSplits = record.amount_splits || [];
          const quantity = Number(record.quantity) || 1;

          let unitAmount = 0;
          let hasRecipient = false;

          if (Array.isArray(amountSplits) && amountSplits.length > 0) {
            const found = amountSplits.find(
              (w: any) => normalizeRecipientName(w.recipient) === recipientName,
            );
            if (found) {
              unitAmount = Number(found.amount) || 0;
              hasRecipient = true;
            }
          }

          if (!hasRecipient || unitAmount <= 0) {
            return '-';
          }

          const totalAmount = unitAmount * quantity;

          return (
            <Typography variant="body2">
              {formatCurrency(totalAmount, payoutObject.currency)}
            </Typography>
          );
        },
      };
    });
  }, [payoutObject, wholesaleRecipientList, wholesaleTotals]);

  const dynamicRecipientColumns = useMemo(() => {
    if (!payoutObject) return [];

    return recipientList.map((recipientName) => {
      const totalForRecipient = recipientTotals[recipientName] || 0;

      return {
        title: (
          <div>
            {recipientName}
            <br />
            <span style={{ fontSize: '12px', color: '#959097' }}>
              (Total: {formatCurrency(totalForRecipient, payoutObject.currency)})
            </span>
          </div>
        ),
        key: `recipient_${recipientName}`,
        render: (_text: any, record: any) => {
          const rawOwnerProfit = Number(record.owner_profit) || 0;
          const wholesaleTotal = getOrderProductWholesaleTotal(record);

          const profitToSplit = Math.max(0, rawOwnerProfit - wholesaleTotal);
          const splits = record.profit_splits || [];

          let percent = 0;
          let hasRecipient = false;

          if (Array.isArray(splits) && splits.length > 0) {
            const found = splits.find(
              (s: any) => normalizeRecipientName(s.recipient) === recipientName,
            );
            if (found) {
              percent = Number(found.percents) || 0;
              hasRecipient = true;
            }
          } else if (recipientName === DEFAULT_RECIPIENT_NAME) {
            percent = 100;
            hasRecipient = true;
          }

          if (!hasRecipient || percent <= 0) {
            return '-';
          }

          const calculatedShare = (profitToSplit * percent) / 100;

          return (
            <Typography variant="body2">
              {formatCurrency(calculatedShare, payoutObject.currency)}{' '}
              <span style={{ color: '#8c8c8c', fontSize: '12px' }}>({percent.toFixed(2)}%)</span>
            </Typography>
          );
        },
      };
    });
  }, [payoutObject, recipientList, recipientTotals, getOrderProductWholesaleTotal]);

  const handlePayoutDetailsExport = useCallback(() => {
    if (!payoutObject || !payoutObject.order_products) return;

    const wholesaleHeaders = wholesaleRecipientList.map((w) => `${w} (Wholesale)`);

    const headerRow = [
      'Item',
      'Delivery date',
      'Item price',
      'Quantity',
      'Total (before fees and taxes)',
      'Refunded amount',
      'Merchant fees',
      'HostCo commission',
      ...wholesaleHeaders,
      ...recipientList,
    ];

    const dataRows = payoutObject.order_products.map((order_product: any) => {
      const refundedAmount = Number(order_product.amount_refunded || 0);
      const hasRefund = refundedAmount > 0;

      const amountSplits = order_product.amount_splits || [];
      const quantity = Number(order_product.quantity) || 1;

      const wholesaleShares = wholesaleRecipientList.map((recipientName) => {
        if (hasRefund) {
          return '0.00';
        }

        if (Array.isArray(amountSplits)) {
          const found = amountSplits.find(
            (w: any) => normalizeRecipientName(w.recipient) === recipientName,
          );
          if (found) {
            const unitAmount = Number(found.amount || 0);
            return (unitAmount * quantity).toFixed(2);
          }
        }
        return '0.00';
      });

      const rawOwnerProfit = Number(order_product.owner_profit) || 0;
      const wholesaleTotal = getOrderProductWholesaleTotal(order_product);
      const profitToSplit = Math.max(0, rawOwnerProfit - wholesaleTotal);

      const splits = order_product.profit_splits || [];

      const recipientShares = recipientList.map((recipientName) => {
        let percent = 0;
        let hasRecipient = false;

        if (Array.isArray(splits) && splits.length > 0) {
          const found = splits.find(
            (s: any) => normalizeRecipientName(s.recipient) === recipientName,
          );
          if (found) {
            percent = Number(found.percents) || 0;
            hasRecipient = true;
          }
        } else if (recipientName === DEFAULT_RECIPIENT_NAME) {
          percent = 100;
          hasRecipient = true;
        }

        if (!hasRecipient || percent <= 0) {
          return '0.00';
        }

        return ((profitToSplit * percent) / 100).toFixed(2);
      });

      return [
        order_product.product_name,
        dayjs(order_product.delivery_date).format('DD MMMM YYYY'),
        Number(order_product.price).toFixed(2),
        order_product.quantity,
        Number(order_product.amount).toFixed(2),
        Number(order_product.amount_refunded || 0).toFixed(2),
        Number(order_product.transaction_fee || 0).toFixed(2),
        Number(order_product.hostco_profit || 0).toFixed(2),
        ...wholesaleShares,
        ...recipientShares,
      ];
    });

    downloadListOfListsAsCSV({
      data: [headerRow, ...dataRows],
      filename: `${payoutObject.store?.store_name || 'store'}_payout_details.csv`,
    });
  }, [payoutObject, recipientList, wholesaleRecipientList, getOrderProductWholesaleTotal]);

  return (
    <Modal
      className={null}
      width={'100%'}
      title={modalTitle}
      visible={showModal}
      footer={null}
      destroyOnClose
      onCancel={handleClose}
    >
      <Typography variant="h6">Purchased products</Typography>
      <Typography variant="caption" as="div">
        * Item Price does not include guest convenience fee. Totals may not be exact due to process
        timing.
      </Typography>
      {owner.plan === PaymentPlan.Basic ? (
        <Typography variant="caption" as="div">
          * <Link href={`https://stripe.com/en-bg/pricing`}>Merchant processing fees</Link> are
          average 2.9% + .30¢ per US transaction. <Link href={`/pricing/`}>Upgrade to Pro</Link> to
          have these fees covered by The Host Co.
        </Typography>
      ) : (
        <Typography variant="caption" as="div">
          * <Link href={`https://stripe.com/en-bg/pricing`}>Merchant processing fees</Link> are
          average 2.9% + .30¢ per US transaction.{' '}
          <strong>Merchant fees are covered by The Host Co for your current payment plan.</strong>
        </Typography>
      )}

      {payoutObject && !isLoading && (
        <>
          <Table
            className="DashboardPayouts_table"
            scroll={{ x: '100%' }}
            dataSource={payoutObject.order_products}
            columns={[
              {
                title: 'Item',
                dataIndex: 'product_name',
                key: 'product_name',
                render: (_text, record) => <>{record.product_name}</>,
              },
              {
                title: 'Delivery date',
                dataIndex: 'delivery_date',
                key: 'delivery_date',
                render: (text, _record) => <>{dayjs(text).format('MMM D, YYYY')}</>,
              },
              {
                title: 'Item price',
                dataIndex: 'price',
                key: 'price',
                render: (_text, record) => (
                  <>{formatCurrency(record.price, payoutObject.currency)}</>
                ),
              },
              {
                title: 'Quantity',
                dataIndex: 'quantity',
                key: 'quantity',
              },

              {
                title: (
                  <div>
                    Total
                    <br />
                    <span style={{ fontSize: '12px', color: '#959097' }}>
                      (before fees and taxes)
                    </span>
                  </div>
                ),
                dataIndex: 'amount',
                key: 'amount',
                render: (_text, record) => (
                  <>{formatCurrency(record.amount, payoutObject.currency)}</>
                ),
              },
              {
                title: 'Refunded amount',
                dataIndex: 'order_product_amount_refunded',
                key: 'order_product_amount_refunded',
                render: (_text, record) => (
                  <>{formatCurrency(record.amount_refunded, payoutObject.currency)}</>
                ),
              },
              {
                title: 'Merchant fees',
                dataIndex: 'transaction_fee',
                key: 'transaction_fee',
                render: (_text, record) => (
                  <>{formatCurrency(record.transaction_fee, payoutObject.currency)}</>
                ),
              },

              {
                title: 'Host Co commission',
                dataIndex: 'hostco_profit',
                key: 'hostco_profit',
                render: (_text, record) => (
                  <>{formatCurrency(record.hostco_profit, payoutObject.currency)}</>
                ),
              },
              ...dynamicWholesaleColumns,
              ...dynamicRecipientColumns,
            ]}
          />
          <div className="DashboardPayouts_table_button_wrapper">
            <Button
              className="DashboardPayouts_table_button"
              variant="contained"
              onClick={handlePayoutDetailsExport}
            >
              Export
            </Button>
          </div>
        </>
      )}
      {payoutObject && !isLoading && !_.isEmpty(payoutObject.applied_balances) && (
        <>
          <Typography variant="h6">Additional transactions</Typography>
          <Typography variant="caption" as="div">
            * Balance amounts are already applied to your total profit from products in this store.
          </Typography>
          <Table
            className="DashboardPayouts_table"
            scroll={{ x: '100%' }}
            dataSource={payoutObject.applied_balances}
            columns={[
              {
                title: 'Note',
                dataIndex: 'external_note',
                key: 'external_note',
              },
              {
                title: 'Balance adjustment',
                dataIndex: 'amount',
                key: 'amount',
                render: (_text, record) => (
                  <>
                    {record.sign === BalanceSign.DECREASE && (
                      <div style={{ color: 'red' }}>
                        -{formatCurrency(record.amount, payoutObject.currency)}
                      </div>
                    )}
                    {record.sign === BalanceSign.INCREASE && (
                      <div style={{ color: 'green' }}>
                        {formatCurrency(record.amount, payoutObject.currency)}
                      </div>
                    )}
                  </>
                ),
              },
              {
                title: 'Date created',
                dataIndex: 'date_created',
                key: 'date_created',
                render: (text, _record) => <>{dayjs(text).format('MMM D, YYYY')}</>,
              },
            ]}
          />
        </>
      )}

      {payoutObject && !isLoading && !_.isEmpty(payoutObject.not_applied_balances) && (
        <>
          <Typography variant="h6">Additional transactions</Typography>
          <Typography variant="caption" as="div">
            * Balance amounts will be applied to your total profit from products in this store at
            the payout day.
          </Typography>
          <Table
            className="DashboardPayouts_table"
            scroll={{ x: '100%' }}
            dataSource={payoutObject.not_applied_balances}
            columns={[
              {
                title: 'Note',
                dataIndex: 'external_note',
                key: 'external_note',
              },
              {
                title: 'Balance adjustment',
                dataIndex: 'leftover_amount',
                key: 'leftover_amount',
                render: (_text, record) => (
                  <>
                    {record.sign === BalanceSign.DECREASE && (
                      <div style={{ color: 'red' }}>
                        -{formatCurrency(record.leftover_amount, payoutObject.currency)}
                      </div>
                    )}
                    {record.sign === BalanceSign.INCREASE && (
                      <div style={{ color: 'green' }}>
                        {formatCurrency(record.leftover_amount, payoutObject.currency)}
                      </div>
                    )}
                  </>
                ),
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default OrderProductDetailsModal;
